import { useContext } from "react";
import { Subscription } from "rxjs";
import { observable, action, computed } from "mobx";
import { SessionEntity, DivisionEntity } from "@citychallenge/api-public";
import { ApiService } from "../api";
import { UserService, LoginError } from "../user";
import { MessageService } from "../message";
import { StorageService } from "../storage";
import { ServiceInterface } from "../service.interface";
import {
  GameModel,
  DivisionModel,
  GameStore,
  ArticleModel,
  DivisionData,
} from "../../models";
import {
  LocalizedError,
  NotFoundError,
  StoryNotFoundError,
} from "../../errors";
import { TransTokens } from "../../i18n/trans-tokens";
import { SessionService } from "../session";
import { createContext } from "react";
import { I18nService } from "../i18n";
import { Loadable, LoadableRequestOptions } from "../../loadable";
import { Environment } from "../../environment";
import { GameSessionModel } from "../../player";
import { DeviceService } from "../device";

interface DivisionConnectParams {
  entity: DivisionEntity;

  startSession?: boolean;
}
interface DivisionSessionSync {
  timestamp: number;
  data: SessionEntity[];
}

const tokens = TransTokens.create({
  notRecognized: "This code is not recognized by the app.",
  errorWithCode: "An error occurred while checking this code: {{error}}",
  noActiveDivision: "Not connected to any company.",
  invalidDomain: "Please enter an approved email address to login.",
  invalidDomainWithDivision:
    "Your current account is not approved by '{{division}}'. \n Please log in with a different email address.",
});

export const divisionTokens = tokens;

export const DivisionServiceContext = createContext<DivisionService>(
  null as any
);

export const useActiveDivision = () => {
  return useContext(DivisionServiceContext).activeDisplayDivision;
};

export class DivisionService implements ServiceInterface {
  public context = DivisionServiceContext;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private storageService: StorageService,
    private messageService: MessageService,
    private sessionService: SessionService,
    private i18nService: I18nService,
    private deviceService: DeviceService
  ) {}

  public KEY_ACTIVE_DIVISION = "ACTIVE_DIVISON";

  /**
   * Internal in milliseconds for which the app refresh the division after reopening
   * the application
   */
  public REFRESH_DIVISION_INTERVAL = 5 * 60 * 1000;

  private subscriptions: Subscription[] = [];

  private _lastUpdate = this.storageService.getPreference<number>(
    "DIVISION_LAST_UPDATE"
  );

  /**
   * Current active division. Use this property for conditional routing.
   */
  @observable
  public activeDivision?: DivisionModel;

  @observable
  private _previousActiveDivision?: DivisionModel;

  /**
   * This property is meant for use in components that display active division,
   * it holds previous active division if there was any,
   * so that elements do not disappear during pop animations.
   */
  @computed
  public get activeDisplayDivision() {
    return this.activeDivision || this._previousActiveDivision;
  }

  @computed
  public get activeDisplayValues() {
    const currentLanguage = this.i18nService.preferredLanguage;
    const translation = this.activeDisplayDivision?.translations?.find(
      (t) => t.language?.id === currentLanguage
    );
    const division = this.activeDisplayDivision;

    return {
      description: translation?.description || division?.description,
      experiencesTabTitle:
        translation?.experiencesTabTitle || division?.experiencesTabTitle,
      articlesTabTitle:
        translation?.articlesTabTitle || division?.articlesTabTitle,
      eventsTabTitle: translation?.eventsTabTitle || division?.eventsTabTitle,
      experiencesWelcomeText:
        translation?.experiencesWelcomeText || division?.experiencesWelcomeText,
      mapBasedTitle: translation?.mapBasedTitle || division?.mapBasedTitle,
      contactButtonTitle:
        translation?.contactButtonTitle || division?.contactButtonTitle,
      contactPageText:
        translation?.contactPageText || division?.contactPageText,
      contactPageImage:
        translation?.contactPageImage || division?.contactPageImage,
    };
  }

  @computed
  public get activeDisplayDivisionDescription() {
    return this.activeDisplayValues.description;
  }

  public setActiveDivision = (division: DivisionData, fromCache = false) => {
    if (this.activeDivision && this.activeDivision.id === division?.id) {
      this.updateDivision(this.activeDivision, division);
      return this.activeDivision;
    }

    const model = this._setActiveDivision(division);

    if (!fromCache) this.resetPreferredLanguage(model);

    // New division connect, reset preferred language to the division base language
    this._updateDivisionStorage(division);

    return model;
  };

  private resetPreferredLanguage = (model: DivisionModel) => {
    const preferred = this.deviceService?.getPreferredLanguages();
    if (preferred?.length && model.supportedLanguages.length) {
      const userLanguages = preferred.map((pref) => pref.short);

      for (const lang of userLanguages) {
        if (model.supportedLanguages.includes(lang)) {
          this.i18nService.setPreferredLanguage(lang);
          return;
        }
      }

      this.i18nService.setPreferredLanguage(model.supportedLanguages[0]);
    }
  };

  public clearActiveDivision = () => {
    this.storageService.removeItem(this.KEY_ACTIVE_DIVISION);
    this._setActiveDivision(undefined);
  };

  private _setActiveDivision(division: DivisionEntity): DivisionModel;
  private _setActiveDivision(division: undefined): undefined;
  @action
  private _setActiveDivision(division: DivisionEntity | undefined) {
    if (this.activeDivision) this._previousActiveDivision = this.activeDivision;

    this.activeDivision = division ? new DivisionModel(division) : undefined;

    return this.activeDivision;
  }

  private _updateDivisionStorage(division?: DivisionEntity) {
    return this.storageService.setItem(
      this.KEY_ACTIVE_DIVISION,
      JSON.stringify(division)
    );
  }

  public async checkActiveDivision() {
    if (this.activeDivision) return this.activeDivision;
    const value = this.storageService.getItem(this.KEY_ACTIVE_DIVISION);

    if (value) {
      this.setActiveDivision(JSON.parse(value), true);
    }
    return this.activeDivision;
  }

  public async serviceWillMount() {
    await this._lastUpdate.ready$;
    // Set last update value equal to now if it does not exist yet
    if (!this._lastUpdate.value) this._lastUpdate.setValue(+new Date());

    await this.checkActiveDivision();
  }

  public serviceWillUnmount() {
    for (const subscription of this.subscriptions) subscription.unsubscribe();
  }

  public serviceDidBecomeForeground() {
    this.refreshActiveDivision();
  }

  public async getDivisionQRCode() {
    if (!this.activeDivision) return;

    const { data } = await this.apiService.clientPublic.getDivisionQRCode(
      this.activeDivision.id
    );

    return data;
  }

  public async getDivisionContact(divisionId: string) {
    const { data } = await this.apiService.clientPublic.contactForDivision(
      divisionId
    );
    return data;
  }

  /**
   * Refresh the active division when ${REFRESH_DIVISION_INTERVAL}ms have expired
   * since the last refresh
   */
  public async refreshActiveDivision() {
    if (!this.activeDivision) return;

    const activeId = this.activeDivision.id;

    if (
      this._lastUpdate.value &&
      +new Date() - this._lastUpdate.value < this.REFRESH_DIVISION_INTERVAL
    )
      return;

    const { data } = await this.apiService.clientPublic.loadDivisionById(
      this.activeDivision.id
    );

    // Check if division is still active, user may have disconnected in the meantime
    if (this.activeDivision?.id === activeId)
      this.updateDivision(this.activeDivision, data);
  }

  public async updateDivision(division: DivisionModel, data: DivisionData) {
    this._lastUpdate.setValue(+new Date());
    this._updateDivisionStorage(data);
    division.update(data);
  }

  public async removeAuthError() {
    if (!this.activeDivision) return;

    this.updateDivision(this.activeDivision, {
      ...this.activeDivision,
      authError: false,
    });
  }

  // check if user can have access to the division that is scanned
  public async authStatus(division: DivisionEntity) {
    if (!this.activeDivision || !this.userService.user) return undefined;

    try {
      return await this.userService.authStatus(division.id);
    } catch (e) {
      if (e instanceof LoginError.InvalidDomainError) {
        this.messageService.show({
          message: tokens.invalidDomainWithDivision,
          values: {
            division: division.name,
          },
          type: "danger",
          duration: 10000,
        });
        return undefined;
      }

      throw e;
    }
  }

  public async passwordlessLogin(email: string) {
    if (!this.activeDivision)
      throw new LocalizedError(undefined, tokens.noActiveDivision);

    try {
      return await this.userService.passwordlessLogin(
        email,
        this.activeDivision.id
      );
    } catch (e) {
      this.rethrowInvalidDomain(email, e);
      throw e;
    }
  }

  public rethrowInvalidDomain(email: string, e: any) {
    if (e instanceof LoginError.InvalidDomainError) {
      throw new LocalizedError(e, tokens.invalidDomain, {
        domain: email.split("@")[1],
        allowed: this.activeDivision?.domains?.map((v) => `"${v}"`).join(" / "),
      });
    }
  }

  public connectToDivision = async ({ entity }: DivisionConnectParams) => {
    try {
      // Because the division is usually set through injection through the HTML
      // page (and passed in with the entity option). We only want to load
      // the division remotely if entity was not set. This is the case for
      // update/refresh requests or the development environment.
      const data = entity;

      this.sessionService.clearActiveSession();

      let authError = false;
      if (data.forceAuthentication) {
        try {
          await this.authStatus(data);
        } catch (e) {
          if (e instanceof LoginError.UnauthenticatedError) {
            authError = true;
          }
        }
      }

      const model = this.setActiveDivision({ ...data, authError });

      // Reset the last update timer
      this._lastUpdate.setValue(+new Date());

      return model;
    } catch (e) {
      if (e.response?.status === 404) {
        this.messageService.show({
          message: tokens.notRecognized,
          type: "danger",
        });
      } else {
        this.messageService.show({
          message: tokens.errorWithCode,
          values: {
            error: e.message,
          },
          type: "danger",
        });
      }

      throw e;
    }
  };

  @observable
  public divisionGames: {
    [key: string]: Loadable<{ games: GameModel[] }>;
  } = {};

  private async fetchDivisionGames(divisionId: string) {
    const games = await this.apiService.clientPublic.gamesOfDivision(
      divisionId
    );

    const mappedGames = games.data.map((game) => {
      return new GameModel(
        game,
        this.storageService.getPreference(`unlocked_${game.id}`)
      );
    });

    return { games: mappedGames };
  }

  public loadableDivisionStore = () => {
    const division = this.activeDisplayDivision;

    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    if (division.loadableGameStore) return division.loadableGameStore;

    division.loadableGameStore = Loadable.create(
      async () => {
        const { gameStore } = division;
        const { games } = await this.fetchDivisionGames(division.id);

        gameStore.setGames(games);

        // Hydrate game store with sessions on refresh
        this.hydrateSessions(gameStore);

        return gameStore;
      },
      {
        refreshInterval: 15 * 60, // Refresh after 15 min of inactivity
        translatedContent: true,
      }
    );

    return division.loadableGameStore;
  };

  private findGame(list: GameModel[], gameId: string) {
    return list.find((g) => g.id == gameId);
  }

  private async fetchGame(gameId: string, options?: LoadableRequestOptions) {
    const { games } = await this.loadableDivisionStore().ensuredValue(options);
    const game = this.findGame(games, gameId);
    if (!game) throw new StoryNotFoundError();
    return game;
  }

  public loadableGame(gameId: string) {
    const { value } = this.loadableDivisionStore();
    const defaultValue = value ? this.findGame(value.games, gameId) : undefined;

    return Loadable.create((options) => this.fetchGame(gameId, options), {
      defaultValue,
      refreshInterval: 15 * 60, // Refresh after 15 min of inactivity
      translatedContent: true,
    });
  }

  /**
   * Hydrate games with sessions fetched from the server for this user
   */
  private async hydrateSessions(gameStore: GameStore) {
    const activeDivision = this.activeDisplayDivision;
    if (!activeDivision)
      throw new LocalizedError(undefined, tokens.noActiveDivision);

    const preference = this.storageService.getPreference<DivisionSessionSync>(
      `SESSIONS_LOADED_${activeDivision.id}`,
      { timestamp: 0, data: [] }
    );

    const { timestamp, data } = await preference.ready$;

    let sessions: SessionEntity[];
    if (timestamp === 0 && this.userService.loggedIn) {
      const response = await this.apiService.clientPrivate.sessionsForDivision(
        activeDivision.id
      );

      sessions = response.data;
      preference.setValue({ timestamp: +new Date(), data: sessions });
    } else {
      sessions = data;
    }

    const allSessions: GameSessionModel[] = [];
    for (const game of gameStore.games) {
      const model = this.sessionService.modelForGame(game, sessions);
      if (model) allSessions.push(model);
    }

    gameStore.setSessions(allSessions);
  }

  /**
   * Article loaders
   */
  public fetchDivisionArticles = async (divisionId: string) => {
    const { data } = await this.apiService.clientPublic.articlesOfDivision(
      divisionId
    );

    return data.map((article) => {
      return new ArticleModel(article);
    });
  };

  public getArticleStore = () => {
    const division = this.activeDisplayDivision;
    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    return division.articleStore;
  };
  public loadableArticleStore = () => {
    const division = this.activeDisplayDivision;
    if (!division) throw new LocalizedError(undefined, tokens.noActiveDivision);

    if (division.loadableArticleStore) return division.loadableArticleStore;

    division.loadableArticleStore = Loadable.create(
      async () => {
        const { articleStore } = division;
        const articles = await this.fetchDivisionArticles(division.id);

        articleStore.setArticles(articles);

        return articleStore;
      },
      {
        translatedContent: true,
        refreshInterval: 15 * 60, // Refresh
      }
    );

    return division.loadableArticleStore;
  };

  public loadableArticle(articleId: string) {
    const { ensuredValue } = this.loadableArticleStore();

    return Loadable.create(async () => {
      const store = await ensuredValue();
      const value = store.findById(articleId);
      if (!value) throw new NotFoundError();
      return value;
    });
  }

  public energyPoints = this.storageService.getPreference<number>(
    "_USER_ENERGYPOINTS",
    0
  );

  public async refreshEnergyPoints() {
    if (Environment.environmentName !== "energyplatform") return;
    if (!this.activeDivision) return;

    const { data } = await this.apiService.clientPrivate.getEnergyPoints(
      this.activeDivision.id
    );
    this.energyPoints.setValue(data.energyPoints);
  }
}
